import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { Unit } from "../../../../services/unitService";
import {
  getUnitSchedules,
  syncUnitSchedules,
  UnitScheduleSlot,
} from "../../../../services/unitScheduleService";

type IntervalRow = {
  localId: string;
  start_time: string;
  end_time: string;
};

type DayScheduleGroup = {
  day_of_week: number;
  enabled: boolean;
  intervals: IntervalRow[];
};

const DEFAULT_START = "09:00";
const DEFAULT_END = "19:00";

const normalizeTimeInput = (value: string): string => {
  if (!value) {
    return "";
  }

  return value.slice(0, 5);
};

const createInterval = (
  start = DEFAULT_START,
  end = DEFAULT_END
): IntervalRow => ({
  localId: `${Date.now()}-${Math.random()}`,
  start_time: start,
  end_time: end,
});

const buildEmptyGroups = (): DayScheduleGroup[] =>
  Array.from({ length: 7 }, (_, dayOfWeek) => ({
    day_of_week: dayOfWeek,
    enabled: false,
    intervals: [createInterval()],
  }));

const groupsFromApi = (slots: UnitScheduleSlot[]): DayScheduleGroup[] => {
  const groups = buildEmptyGroups();

  slots.forEach((slot) => {
    const group = groups.find((item) => item.day_of_week === slot.day_of_week);

    if (!group) {
      return;
    }

    group.enabled = true;

    if (
      group.intervals.length === 1 &&
      !group.intervals[0].start_time &&
      !group.intervals[0].end_time
    ) {
      group.intervals = [];
    }

    group.intervals.push({
      localId: `${slot.day_of_week}-${slot.start_time}-${slot.end_time}`,
      start_time: normalizeTimeInput(slot.start_time),
      end_time: normalizeTimeInput(slot.end_time),
    });
  });

  groups.forEach((group) => {
    if (group.enabled && group.intervals.length === 0) {
      group.intervals = [createInterval()];
    }
  });

  return groups;
};

interface UnitScheduleModalProps {
  unit: Unit | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const UnitScheduleModal = ({ unit, isOpen, onClose, onSaved }: UnitScheduleModalProps) => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<DayScheduleGroup[]>(buildEmptyGroups);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const dayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, dayOfWeek) =>
        t(`professionals.schedule.days.${dayOfWeek}`)
      ),
    [t]
  );

  const loadGroups = useCallback(async () => {
    if (!unit) {
      return;
    }

    setLoading(true);

    try {
      const response = await getUnitSchedules(unit.id);
      setGroups(groupsFromApi(response.data));
    } catch (error: any) {
      toast.error(error?.data || t("studio.units.schedules.toast.loadError"));
      setGroups(buildEmptyGroups());
    } finally {
      setLoading(false);
    }
  }, [unit, t]);

  useEffect(() => {
    if (!isOpen || !unit) {
      return;
    }

    loadGroups();
  }, [isOpen, unit, loadGroups]);

  const updateGroup = (dayOfWeek: number, patch: Partial<DayScheduleGroup>) => {
    setGroups((previous) =>
      previous.map((group) =>
        group.day_of_week === dayOfWeek ? { ...group, ...patch } : group
      )
    );
  };

  const updateInterval = (
    dayOfWeek: number,
    localId: string,
    patch: Partial<IntervalRow>
  ) => {
    setGroups((previous) =>
      previous.map((group) => {
        if (group.day_of_week !== dayOfWeek) {
          return group;
        }

        return {
          ...group,
          intervals: group.intervals.map((interval) =>
            interval.localId === localId ? { ...interval, ...patch } : interval
          ),
        };
      })
    );
  };

  const addInterval = (dayOfWeek: number) => {
    setGroups((previous) =>
      previous.map((group) =>
        group.day_of_week === dayOfWeek
          ? { ...group, intervals: [...group.intervals, createInterval()] }
          : group
      )
    );
  };

  const removeInterval = (dayOfWeek: number, localId: string) => {
    setGroups((previous) =>
      previous.map((group) => {
        if (group.day_of_week !== dayOfWeek) {
          return group;
        }

        const nextIntervals = group.intervals.filter((interval) => interval.localId !== localId);

        return {
          ...group,
          intervals: nextIntervals.length > 0 ? nextIntervals : [createInterval()],
        };
      })
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!unit) {
      return;
    }

    const schedules = groups
      .filter((group) => group.enabled)
      .flatMap((group) =>
        group.intervals.map((interval) => ({
          day_of_week: group.day_of_week,
          start_time: interval.start_time,
          end_time: interval.end_time,
        }))
      );

    for (const schedule of schedules) {
      if (!schedule.start_time || !schedule.end_time) {
        toast.error(t("studio.units.schedules.validation.timesRequired"));
        return;
      }

      if (schedule.end_time <= schedule.start_time) {
        toast.error(t("studio.units.schedules.validation.endAfterStart"));
        return;
      }
    }

    const groupedByDay = schedules.reduce<Record<number, typeof schedules>>((accumulator, slot) => {
      if (!accumulator[slot.day_of_week]) {
        accumulator[slot.day_of_week] = [];
      }

      accumulator[slot.day_of_week].push(slot);

      return accumulator;
    }, {});

    for (const daySlots of Object.values(groupedByDay)) {
      const sorted = [...daySlots].sort((left, right) => left.start_time.localeCompare(right.start_time));

      for (let index = 1; index < sorted.length; index += 1) {
        if (sorted[index].start_time < sorted[index - 1].end_time) {
          toast.error(t("studio.units.schedules.validation.overlap"));
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      const response = await syncUnitSchedules(unit.id, { schedules });
      toast.success(response.message || t("studio.units.schedules.toast.saveSuccess"));
      setGroups(groupsFromApi(response.data));
      onSaved?.();
      onClose();
    } catch (error: any) {
      toast.error(error?.data || t("studio.units.schedules.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered className="unit-schedule-modal">
      <ModalHeader toggle={onClose}>
        {unit
          ? t("studio.units.schedules.title", { name: unit.name })
          : t("studio.units.schedules.titleFallback")}
      </ModalHeader>
      <Form onSubmit={handleSubmit} className="unit-schedule-modal__form d-flex flex-column">
        <ModalBody className="unit-schedule-modal__body overflow-auto">
          <p className="text-muted">{t("studio.units.schedules.description")}</p>

          {loading ? (
            <div className="text-center py-4">
              <Spinner color="primary">{t("studio.units.schedules.loading")}</Spinner>
            </div>
          ) : (
            <div className="d-flex flex-column gap-4">
              {groups.map((group) => (
                <div key={group.day_of_week} className="border rounded p-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="form-check mb-0">
                      <Input
                        type="checkbox"
                        id={`unit-schedule-day-${group.day_of_week}`}
                        checked={group.enabled}
                        onChange={(event) =>
                          updateGroup(group.day_of_week, { enabled: event.target.checked })
                        }
                      />
                      <Label
                        check
                        htmlFor={`unit-schedule-day-${group.day_of_week}`}
                        className="fw-medium ms-1"
                      >
                        {dayLabels[group.day_of_week]}
                      </Label>
                    </div>
                    {group.enabled ? (
                      <Button
                        color="soft-primary"
                        size="sm"
                        type="button"
                        onClick={() => addInterval(group.day_of_week)}
                      >
                        <i className="ri-add-line me-1" />
                        {t("studio.units.schedules.actions.addInterval")}
                      </Button>
                    ) : null}
                  </div>

                  {group.enabled ? (
                    <div className="d-flex flex-column gap-2">
                      {group.intervals.map((interval, index) => (
                        <div key={interval.localId} className="row g-2 align-items-end">
                          <div className="col-sm-5">
                            <Label className="form-label mb-1">
                              {t("studio.units.schedules.table.start")}
                            </Label>
                            <Input
                              type="time"
                              value={interval.start_time}
                              onChange={(event) =>
                                updateInterval(group.day_of_week, interval.localId, {
                                  start_time: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-sm-5">
                            <Label className="form-label mb-1">
                              {t("studio.units.schedules.table.end")}
                            </Label>
                            <Input
                              type="time"
                              value={interval.end_time}
                              onChange={(event) =>
                                updateInterval(group.day_of_week, interval.localId, {
                                  end_time: event.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="col-sm-2">
                            {group.intervals.length > 1 ? (
                              <Button
                                color="soft-danger"
                                size="sm"
                                type="button"
                                className="w-100"
                                onClick={() => removeInterval(group.day_of_week, interval.localId)}
                                aria-label={t("studio.units.schedules.actions.removeInterval", {
                                  index: index + 1,
                                })}
                              >
                                <i className="ri-delete-bin-line" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0 small">
                      {t("studio.units.schedules.dayDisabled")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="unit-schedule-modal__footer flex-shrink-0 border-top bg-body">
          <Button color="light" type="button" onClick={onClose} disabled={submitting}>
            {t("units.actions.cancel")}
          </Button>
          <Button color="primary" type="submit" disabled={loading || submitting}>
            {submitting ? (
              <Spinner size="sm" className="me-1" />
            ) : (
              <i className="ri-save-line align-bottom me-1" />
            )}
            {t("studio.units.schedules.actions.save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UnitScheduleModal;
